// client/src/components/team/InviteTeamMemberModal.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Mail, Shield, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { wholesalerAPI } from '../../services/api';

const InviteTeamMemberModal = ({ isOpen, onClose, onInvited, memberCount, memberLimit, isUnlimited }) => {
  const { t } = useTranslation('wholesaler');
  const [email, setEmail] = useState('');
  const [subRole, setSubRole] = useState('office');
  const [sending, setSending] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      await wholesalerAPI.inviteTeamMember({ email, subRole });
      toast.success(t('team.inviteModal.success'));
      setEmail('');
      setSubRole('office');
      onInvited();
      onClose();
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to send invitation';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const atLimit = !isUnlimited && memberCount >= memberLimit;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">{t('team.inviteModal.title')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Limit warning */}
          {atLimit && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              {t('team.inviteModal.limitReached')}
            </div>
          )}

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Mail size={14} className="inline mr-1" />
              {t('team.inviteModal.emailLabel')}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="colleague@example.com"
            />
          </div>

          {/* Sub-role selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Shield size={14} className="inline mr-1" />
              {t('team.inviteModal.roleLabel')}
            </label>
            <div className="space-y-3">
              {/* Office Personnel */}
              <label
                className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                  subRole === 'office' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="subRole"
                  value="office"
                  checked={subRole === 'office'}
                  onChange={(e) => setSubRole(e.target.value)}
                  className="mt-1 mr-3 text-purple-600"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">{t('team.subRoles.office')}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{t('team.subRoleDesc.office')}</p>
                </div>
              </label>

              {/* Sales Rep */}
              <label
                className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                  subRole === 'sales_rep' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="subRole"
                  value="sales_rep"
                  checked={subRole === 'sales_rep'}
                  onChange={(e) => setSubRole(e.target.value)}
                  className="mt-1 mr-3 text-purple-600"
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">{t('team.subRoles.sales_rep')}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{t('team.subRoleDesc.salesRep')}</p>
                </div>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              {t('common:cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={sending || atLimit}
              className="px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Users size={14} />
              {sending ? t('team.inviteModal.sending') : t('team.inviteModal.send')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteTeamMemberModal;
